/**
 * Capture Buffers API — the server-side pre-generation buffer (ADR-030 §9).
 *
 * The `Captured` state: photos, notes and streaming transcripts for a capture
 * that has not been generated yet. These used to live only in this browser's
 * IndexedDB, so an in-progress capture died with the device; they are now
 * tenant-scoped rows owned by the author and TTL-expired after 30 days.
 *
 * Photos travel as a `mediaId` only. The bytes are already in the tenant media
 * store (AddContext uploads on capture to obtain the id) and the server stores
 * the reference, never the blob — so a restore resolves images through
 * `mediaApi.getMedia`, which does its own ownership check.
 */

import axios from 'axios';
import { SYSTEMDOX_API_URL } from '@/lib/apiConfig';

const api = axios.create({ baseURL: SYSTEMDOX_API_URL });

/** A photo in a server buffer: a reference, never bytes. */
export interface BufferPhoto {
  mediaId: string;
  timestamp: number;
  description?: string;
  mermaid?: string | null;
}

export interface BufferNote {
  id: string;
  text: string;
  timestamp: number;
  sourceType?: string;
  sourceTitle?: string;
}

export interface BufferItems {
  photos: BufferPhoto[];
  notes: BufferNote[];
  streamingTranscripts: Array<{ transcript: string; duration: number }>;
}

/** List shape — counts, not content. */
export interface BufferSummary {
  id: string;
  title: string;
  updatedAt: string;
  inputMethod: string;
  photoCount: number;
  noteCount: number;
  streamingTranscriptCount: number;
}

export interface BufferDetail {
  id: string;
  title: string;
  inputMethod: string;
  languageCode: string;
  templateIds: string[];
  createdAt: string;
  updatedAt: string;
  items: BufferItems;
}

export interface SaveBufferRequest {
  title: string;
  inputMethod: string;
  languageCode: string;
  templateIds?: string[];
  items: BufferItems;
}

export const captureBuffersApi = {
  setAuthProvider(getToken: () => Promise<string | null>) {
    api.interceptors.request.use(async (config) => {
      const token = await getToken();
      if (token) config.headers.Authorization = `Bearer ${token}`;
      return config;
    });
  },

  /** The caller's own in-progress captures, newest first. */
  async list(): Promise<BufferSummary[]> {
    const { data } = await api.get('/capture-buffers');
    return data.buffers ?? [];
  },

  async get(bufferId: string): Promise<BufferDetail> {
    const { data } = await api.get(`/capture-buffers/${bufferId}`);
    return data.buffer;
  },

  /** Upsert — idempotent, so a retried autosave is harmless. */
  async put(bufferId: string, body: SaveBufferRequest): Promise<void> {
    await api.put(`/capture-buffers/${bufferId}`, body);
  },

  async remove(bufferId: string): Promise<void> {
    await api.delete(`/capture-buffers/${bufferId}`);
  },
};
